import { MATHML_TAGS } from '@/constants/mathml'
import { MathContent } from '@/kontent/content-types'
import { extractGlossaryILinksFromHTML } from '@/utils/parseGlossaryLinksFromHTML'
import { waitUntil } from '@/utils/waitUntil'
import type { Elements } from '@kontent-ai/delivery-sdk'
import { log } from 'next-axiom'
import Script from 'next/script'
import { useEffect, useMemo } from 'react'
import sanitizeHtml from 'sanitize-html'
import type { RichtextSectionProps } from '.'
import RichText from '../RichText'

// Allow default tags plus MathML tags for math content
const ALLOWED_TAGS = sanitizeHtml.defaults.allowedTags.concat(...MATHML_TAGS)

const getTypesetPromise = async () => {
	const typesetPromise = await new Promise((resolve) => {
		waitUntil(
			() => (window as any).MathJax?.typesetPromise,
			() => {
				resolve((window as any).MathJax?.typesetPromise)
			},
		)
	})
	return typesetPromise as any
}

const typeset = async () => {
	const typesetPromise = await getTypesetPromise()
	if (typesetPromise && (window as any).MathJaxPromise) {
		if ((window as any).MathJaxPromise) {
			;(window as any).MathJaxPromise = (
				window as any
			).MathJaxPromise.then(() => {
				return typesetPromise()
			})
		}
	} else {
		if (!(window as any).MathJaxPromise) {
			;(window as any).MathJaxPromise = Promise.resolve()
			typeset()
		}
	}
}

function MathContentComponent({
	linkedItem,
	mappings,
	linkedItems,
	currentPath,
	currentStage,
	currentYear,
	currentSyllabus,
	currentKeyLearningAreas,
	isLifeSkillMode,
	resolveFootnotesLink,
	pageType,
}: RichtextSectionProps<MathContent>) {
	// Pre-process rich text element:
	// 1. Sanitize HTML (allow MathML tags)
	// 2. Add custom glossary links as ILink objects
	// This only affects math_content data and doesn't impact other uses of RichText
	const enhancedRichTextElement = useMemo(() => {
		const richTextElement = linkedItem.elements
			.content as Elements.RichTextElement

		// Sanitize HTML value with MathML support
		const sanitizedValue = sanitizeHtml(richTextElement.value, {
			allowedTags: ALLOWED_TAGS,
			allowedAttributes: false, // Allow all attributes
		})

		const customGlossaryLinks =
			extractGlossaryILinksFromHTML(sanitizedValue)

		// If no custom glossary links, return with sanitized value only
		if (customGlossaryLinks.length === 0) {
			return {
				...richTextElement,
				value: sanitizedValue,
			}
		}

		const existingLinks = richTextElement.links || []
		const existingLinkIds = new Set(
			existingLinks.map((link) => link.linkId),
		)
		const uniqueCustomLinks = customGlossaryLinks.filter(
			(link) => !existingLinkIds.has(link.linkId),
		)

		return {
			...richTextElement,
			value: sanitizedValue,
			links: [...existingLinks, ...uniqueCustomLinks],
		}
	}, [linkedItem.elements.content])

	useEffect(() => {
		try {
			typeset()
		} catch (error) {
			log.error('MathJax failed to load', {
				...error,
				request: {
					host: window.location.host || '',
				},
				vercel: {
					deploymentUrl: process.env.NEXT_PUBLIC_VERCEL_URL || '',
				},
			})
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [linkedItem.elements.content.value])

	return enhancedRichTextElement.value ? (
		<>
			<RichText
				css={{
					'&&:not(:nth-child(-n+2))': {
						marginTop: '2.25rem',
					},
					[`.TeachingSupportCard &&:not(:nth-child(-n+2)),
						.glossary-body__definition &&:not(:nth-child(-n+2))`]: {
						marginTop: '1rem',
					},
				}}
				richTextElement={enhancedRichTextElement}
				linkedItems={linkedItems}
				mappings={mappings}
				currentPath={currentPath}
				currentStage={currentStage}
				currentYear={currentYear}
				currentSyllabus={currentSyllabus}
				currentKeyLearningAreas={currentKeyLearningAreas}
				isLifeSkillMode={isLifeSkillMode}
				resolveFootnotesLink={resolveFootnotesLink}
				pageType={pageType}
			/>
			<Script
				strategy="afterInteractive"
				src="https://cdn.jsdelivr.net/npm/mathjax@4.0.0-beta.6/tex-mml-svg.js"
			/>
		</>
	) : null
}

// Export with the original name for compatibility with the sections index
export default MathContentComponent
